import { getTextElementById } from '../utils';
import { TrackType } from '@/player-config/src/types/Track/Type';
import { TextElement } from '@/player-config/src/types/RemotionPlayerConfigTypes/Element/TextElement';
import { updatePlayerConfigWithTracks } from '@/Pages/Clip/ClipPlayer/ClipAssets/utils';
import { ElementType } from '@/player-config/src/types/Element/Type';
import { saveClipChanges, updateClipMetadataWithId } from '@/stores/clip';
import { Clip } from '@/player-config/src/types/clip-types';

export const generateDropShadow = (offsetX: number, offsetY: number, blur: number, color: string): string => {
  return `${offsetX}px ${offsetY}px ${blur}px ${color}`;
};

export const updateTextElementProperties = (
  clipId: string,
  clipData: Clip,
  textElementId: string,
  updates: Partial<Pick<TextElement, 'containerStyle' | 'style' | 'transitions'>>,
  shouldAddToHistory: boolean = true
) => {
  const playerConfig = clipData.asset_metadata.player_config;
  if (!playerConfig) {
    return;
  }

  // Custom implementation for TextElement properties (supports textStyle and transitions)
  const updatedTracks = playerConfig.playerConfig?.tracks.map((track: any) => {
    if (track.type !== TrackType.ELEMENTS || !Array.isArray(track.elements)) {
      return track;
    }

    const updatedElements = track.elements.map((element: TextElement) => {
      if (element.id !== textElementId) {
        return element;
      }

      return {
        ...element,
        ...(updates.containerStyle && {
          containerStyle: {
            ...element.containerStyle,
            ...updates.containerStyle
          }
        }),
        ...(updates.style && {
          style: {
            ...element.style,
            ...updates.style
          }
        }),
        ...(updates.transitions && {
          transitions: {
            ...element.transitions,
            ...updates.transitions
          }
        }),
        metadata: undefined
      };
    });

    return {
      ...track,
      elements: updatedElements
    };
  });

  const updatedConfig = updatePlayerConfigWithTracks(playerConfig, updatedTracks);
  updateClipMetadataWithId(clipId, { player_config: updatedConfig });
  saveClipChanges({ clipId, shouldGenerateThumbnail: false, shouldAddToHistory });
};

export const updateTextElementShadow = (
  clipId: string,
  clipData: Clip,
  textElementId: string,
  blur: number,
  color: string
) => {
  const offsetX = Math.round(blur * 0.6);
  const offsetY = Math.round(blur * 0.6);
  const dropShadow = generateDropShadow(offsetX, offsetY, blur, color);

  // Check if element has existing outline (multi-shadow)
  const selectedElement = getTextElementById(clipData, textElementId);
  const existingTextShadow = selectedElement?.style?.textShadow;

  let combinedShadow = dropShadow;

  // If there's existing outline shadow, combine them
  if (existingTextShadow && existingTextShadow.includes('-0.5px')) {
    // This is an outline shadow, preserve it and add drop shadow
    combinedShadow = `${existingTextShadow}, ${dropShadow}`;
  }

  updateTextElementProperties(clipId, clipData, textElementId, {
    style: { textShadow: combinedShadow }
  });
};

export const removeTextElementShadow = (clipId: string, clipData: Clip, textElementId: string) => {
  updateTextElementProperties(clipId, clipData, textElementId, {
    style: { textShadow: undefined }
  });
};

export const getTextElementShadow = (element: TextElement): { blur: number; color: string } => {
  const textShadow = element.style?.textShadow || '0px 0px 0px #000000';
  const [, , b = '0px', c = '#000000'] = textShadow.split(' ');
  return {
    blur: parseInt(b) || 0,
    color: c
  };
};

export const updateTextElementOutline = (
  clipId: string,
  clipData: Clip,
  textElementId: string,
  width: number,
  color: string
) => {
  updateTextElementProperties(clipId, clipData, textElementId, {
    style: {
      WebkitTextStrokeWidth: `${width}px`,
      WebkitTextStrokeColor: color,
      paintOrder: 'stroke'
    }
  });
};

export const removeTextElementOutline = (clipId: string, clipData: Clip, textElementId: string) => {
  updateTextElementProperties(clipId, clipData, textElementId, {
    style: {
      WebkitTextStrokeWidth: undefined,
      WebkitTextStrokeColor: undefined,
      paintOrder: undefined
    }
  });
};

export const getTextElementOutline = (element: TextElement): { width: number; color: string } => {
  const width = parseInt(element?.style?.WebkitTextStrokeWidth || '0');
  const color = element?.style?.WebkitTextStrokeColor || '#000000';

  return {
    width,
    color
  };
};

export const updateTextFontFamily = (
  clipId: string,
  clipData: Clip,
  textElementId: string,
  fontName: string,
  fontLocation: string
) => {
  const playerConfig = clipData.asset_metadata.player_config;
  if (!playerConfig) {
    return;
  }

  // Custom implementation for TextElement properties
  const updatedTracks = playerConfig.playerConfig?.tracks.map((track: any) => {
    if (track.type !== TrackType.ELEMENTS || !Array.isArray(track.elements)) {
      return track;
    }

    const updatedElements = track.elements.map((element: any) => {
      if (element.id !== textElementId) {
        return element;
      }

      return {
        ...element,
        style: {
          ...element.style,
          fontFamily: fontName
        },
        details: {
          ...element.details,
          font: {
            name: fontName,
            location: fontLocation
          }
        }
      };
    });

    return {
      ...track,
      elements: updatedElements
    };
  });

  const updatedConfig = updatePlayerConfigWithTracks(playerConfig, updatedTracks);
  updateClipMetadataWithId(clipId, { player_config: updatedConfig });
  saveClipChanges({ clipId, shouldGenerateThumbnail: false, shouldAddToHistory: true });
};

export const toggleCaptions = (clipId: string, clipData: Clip, textElementId: string) => {
  const playerConfig = clipData.asset_metadata.player_config;
  if (!playerConfig) {
    return;
  }

  // Update the element details in the tracks
  const updatedTracks = playerConfig.playerConfig?.tracks.map((track: any) => {
    if (track.type !== TrackType.ELEMENTS || !Array.isArray(track.elements)) {
      return track;
    }

    const updatedElements = track.elements.map((element: { id: string; details: { areCaptionsHidden: boolean } }) => {
      if (element.id !== textElementId) {
        return element;
      }

      return {
        ...element,
        details: {
          ...element.details,
          areCaptionsHidden: !element.details.areCaptionsHidden
        }
      };
    });

    return {
      ...track,
      elements: updatedElements
    };
  });

  const updatedConfig = updatePlayerConfigWithTracks(playerConfig, updatedTracks);

  updateClipMetadataWithId(clipId, { player_config: updatedConfig });
  saveClipChanges({ clipId, shouldGenerateThumbnail: false, shouldAddToHistory: true });
};

export const deleteTextElement = (clipId: string, clipData: Clip, textElementId: string) => {
  const playerConfig = clipData.asset_metadata.player_config;
  if (!playerConfig) {
    return;
  }

  const updatedTracks = playerConfig.playerConfig?.tracks.map((track: any) => {
    if (track.type !== TrackType.ELEMENTS || !Array.isArray(track.elements)) {
      return track;
    }

    return {
      ...track,
      elements: track.elements.filter((element: TextElement) => element.id !== textElementId)
    };
  });

  const updatedConfig = updatePlayerConfigWithTracks(playerConfig, updatedTracks);
  updateClipMetadataWithId(clipId, { player_config: updatedConfig });
  saveClipChanges({ clipId, shouldGenerateThumbnail: false, shouldAddToHistory: true });
};

export const updateTextElementText = (clipId: string, clipData: Clip, textElementId: string, newText: string) => {
  const playerConfig = clipData.asset_metadata.player_config;
  if (!playerConfig) {
    return;
  }

  // Custom implementation for TextElement text content
  const updatedTracks = playerConfig.playerConfig?.tracks.map((track: any) => {
    if (track.type !== TrackType.ELEMENTS || !Array.isArray(track.elements)) {
      return track;
    }

    const updatedElements = track.elements.map((element: TextElement) => {
      if (element.id !== textElementId) {
        return element;
      }

      return {
        ...element,
        name: newText,
        details: {
          ...element.details,
          text: newText
        }
      };
    });

    return {
      ...track,
      elements: updatedElements
    };
  });

  const updatedConfig = updatePlayerConfigWithTracks(playerConfig, updatedTracks);
  updateClipMetadataWithId(clipId, { player_config: updatedConfig });
  saveClipChanges({ clipId, shouldGenerateThumbnail: false, shouldAddToHistory: true });
};

export const applySelectedTextElementStylesToAllTextElements = (
  clipId: string,
  clipData: Clip,
  textElementId: string,
  shouldAddToHistory: boolean = true
) => {
  const playerConfig = clipData.asset_metadata.player_config;
  if (!playerConfig) {
    return;
  }

  const selectedElement = getTextElementById(clipData, textElementId);

  if (!selectedElement) {
    return;
  }

  const updatedTracks = playerConfig.playerConfig?.tracks.map((track: any) => {
    if (track.type !== TrackType.ELEMENTS || !Array.isArray(track.elements)) {
      return track;
    }

    const updatedElements = track.elements.map((element: TextElement) => {
      if (element.type !== ElementType.TEXT) {
        return element;
      }

      return {
        ...element,
        details: {
          ...element.details,
          font: { ...selectedElement.details.font },
          areCaptionsHidden: selectedElement.details.areCaptionsHidden
        },
        containerStyle: { ...selectedElement.containerStyle },
        style: { ...selectedElement.style },
        transitions: { ...selectedElement.transitions }
      };
    });

    return {
      ...track,
      elements: updatedElements
    };
  });

  const updatedConfig = updatePlayerConfigWithTracks(playerConfig, updatedTracks);
  updateClipMetadataWithId(clipId, { player_config: updatedConfig });
  saveClipChanges({ clipId, shouldGenerateThumbnail: false, shouldAddToHistory });
};
